import { useEffect, useMemo, useState } from 'react'
import {
    MdCheck,
    MdMeetingRoom,
    MdPersonOff,
    MdSchedule,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import ConfirmModal from '@/components/confirm-modal'
import OptionDropdown from './option-dropdown'
import { getRooms, Room } from '@/lib/api/rooms/rooms-api'
import {
    APPOINTMENT_KIND_LABELS,
    finishPresenceEntry,
    presenceBadgeProps,
    PresenceEntry,
    PresenceStatus,
    updatePresenceEntry,
} from '@/lib/api/presence/presence-api'

interface PresenceListProps {
    entries: PresenceEntry[]
    statuses: PresenceStatus[]
    loading: boolean
    /** Somente leitura: a tela aberta na sala do profissional não opera o painel. */
    readOnly?: boolean
    onChanged: () => void
    /** Layout ampliado, usado na tela exclusiva. */
    dense?: boolean
}

/**
 * Origem do agendamento que gerou a presença, se houver.
 *
 * `description` já vem pronta para exibir: agendamento sem título próprio mostra
 * só o tipo, e não o "Consulta: Consulta" que a concatenação cega produzia.
 */
function originOf(entry: PresenceEntry): {
    description: string
    professional: string | null
} | null {
    const describe = (label: string, title: string | null) =>
        title && title.trim() && title.trim() !== label
            ? `${label}: ${title.trim()}`
            : label

    if (entry.consultation) {
        return {
            description: describe(
                APPOINTMENT_KIND_LABELS.CONSULTATION,
                entry.consultation.title
            ),
            professional: entry.consultation.professional?.name ?? null,
        }
    }
    if (entry.exam) {
        return {
            description: describe(
                APPOINTMENT_KIND_LABELS.EXAM,
                entry.exam.title
            ),
            professional: entry.exam.professional?.name ?? null,
        }
    }
    if (entry.order) {
        return {
            description: describe(
                APPOINTMENT_KIND_LABELS.ORDER,
                entry.order.title
            ),
            professional: entry.order.professional?.name ?? null,
        }
    }
    return null
}

function formatTime(value: string | null): string {
    if (!value) return '--:--'
    return new Date(value).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
    })
}

/** Há quanto tempo o paciente está esperando. */
function waitingFor(arrivedAt: string): string {
    const minutes = Math.max(
        0,
        Math.floor((Date.now() - new Date(arrivedAt).getTime()) / 60000)
    )
    if (minutes < 60) return `${minutes} min`
    const hours = Math.floor(minutes / 60)
    return `${hours}h ${minutes % 60}min`
}

export default function PresenceList({
    entries,
    statuses,
    loading,
    readOnly = false,
    onChanged,
    dense = false,
}: PresenceListProps) {
    const [savingId, setSavingId] = useState<number | null>(null)
    const [entryToFinish, setEntryToFinish] = useState<PresenceEntry | null>(null)
    const [rooms, setRooms] = useState<Room[]>([])

    useEffect(() => {
        if (readOnly) return
        getRooms().then(response => {
            if (response.success && response.result) {
                setRooms(response.result.filter(room => room.active))
            }
        })
    }, [readOnly])

    const activeStatuses = useMemo(
        () => statuses.filter(status => status.active),
        [statuses]
    )

    const handleStatusChange = async (entry: PresenceEntry, statusId: number) => {
        if (statusId === entry.statusId) return
        setSavingId(entry.id)
        const response = await updatePresenceEntry(entry.id, { statusId })
        setSavingId(null)
        if (!response.success) {
            toast.error(response.error || 'Não foi possível trocar o controle')
            return
        }
        onChanged()
    }

    const handleRoomChange = async (
        entry: PresenceEntry,
        roomId: number | null
    ) => {
        if (roomId === (entry.roomId ?? null)) return
        setSavingId(entry.id)
        const response = await updatePresenceEntry(entry.id, { roomId })
        setSavingId(null)
        if (!response.success) {
            toast.error(response.error || 'Não foi possível trocar a sala')
            return
        }
        onChanged()
    }

    const handleFinish = async () => {
        if (!entryToFinish) return
        setSavingId(entryToFinish.id)
        const response = await finishPresenceEntry(entryToFinish.id)
        setSavingId(null)
        setEntryToFinish(null)
        if (!response.success) {
            toast.error(response.error || 'Não foi possível encerrar a presença')
            return
        }
        toast.success('Presença encerrada')
        onChanged()
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <span className="loading loading-spinner loading-md" />
            </div>
        )
    }

    if (entries.length === 0) {
        return (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
                <MdPersonOff className="text-4xl text-base-content/30" />
                <p className="text-sm font-medium">Ninguém no controle de presença</p>
                <p className="text-xs text-base-content/60">
                    Use a aba "Definir controle" para registrar quem chegou.
                </p>
            </div>
        )
    }

    return (
        <>
            <ul className="flex flex-col gap-2">
                {entries.map((entry, index) => {
                    const origin = originOf(entry)
                    const badge = presenceBadgeProps(entry.status.color)

                    return (
                        <li
                            key={entry.id}
                            className={`flex flex-col gap-3 rounded-xl border border-base-200 bg-base-100 p-3 sm:flex-row sm:items-center ${
                                dense ? 'sm:p-4' : ''
                            }`}
                        >
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-base-200 text-sm font-semibold">
                                {index + 1}
                            </span>

                            <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="truncate font-semibold">
                                        {entry.client.name}
                                    </span>
                                    <span
                                        className={`badge badge-sm ${badge.className}`}
                                        style={badge.style}
                                    >
                                        {entry.status.name}
                                    </span>
                                </div>

                                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-base-content/70">
                                    {origin && (
                                        <span className="truncate">
                                            {origin.description}
                                        </span>
                                    )}
                                    {origin?.professional && (
                                        <span className="truncate">
                                            {origin.professional}
                                        </span>
                                    )}
                                    <span className="flex items-center gap-1">
                                        <MdSchedule className="text-sm" />
                                        {entry.scheduledAt
                                            ? `Agendado ${formatTime(entry.scheduledAt)}`
                                            : 'Sem horário'}
                                    </span>
                                    <span>
                                        Chegou {formatTime(entry.arrivedAt)} · espera{' '}
                                        {waitingFor(entry.arrivedAt)}
                                    </span>
                                    {entry.room && (
                                        <span className="flex items-center gap-1">
                                            <MdMeetingRoom className="text-sm" />
                                            {entry.room.name}
                                        </span>
                                    )}
                                </div>

                                {entry.notes && (
                                    <p className="mt-1 text-xs text-base-content/60">
                                        {entry.notes}
                                    </p>
                                )}
                            </div>

                            {!readOnly && (
                                <div className="flex shrink-0 items-center gap-2">
                                    <OptionDropdown
                                        ariaLabel="Controle de presença"
                                        className="w-52"
                                        value={entry.statusId}
                                        disabled={savingId === entry.id}
                                        options={activeStatuses.map(status => ({
                                            value: status.id,
                                            label: `${status.position}. ${status.name}`,
                                            color: status.color,
                                        }))}
                                        onChange={value =>
                                            handleStatusChange(entry, Number(value))
                                        }
                                    />

                                    <OptionDropdown
                                        ariaLabel="Sala / consultório"
                                        className="w-44"
                                        placeholder="Sem sala"
                                        value={entry.roomId ?? ''}
                                        disabled={savingId === entry.id}
                                        options={[
                                            { value: '', label: 'Sem sala' },
                                            ...rooms.map(room => ({
                                                value: room.id,
                                                label: room.name,
                                                color: room.color,
                                                hint: room.description,
                                            })),
                                        ]}
                                        onChange={value =>
                                            handleRoomChange(
                                                entry,
                                                value === '' ? null : Number(value)
                                            )
                                        }
                                    />

                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm !rounded-lg"
                                        title="Encerrar presença"
                                        disabled={savingId === entry.id}
                                        onClick={() => setEntryToFinish(entry)}
                                    >
                                        {savingId === entry.id ? (
                                            <span className="loading loading-spinner loading-xs" />
                                        ) : (
                                            <MdCheck className="text-lg" />
                                        )}
                                    </button>
                                </div>
                            )}
                        </li>
                    )
                })}
            </ul>

            <ConfirmModal
                isOpen={!!entryToFinish}
                onClose={() => setEntryToFinish(null)}
                onConfirm={handleFinish}
                title="Encerrar presença"
                message={`Tirar ${entryToFinish?.client.name ?? ''} do controle de presença? O registro fica no histórico do dia.`}
                confirmButtonText="Encerrar"
                confirmButtonClass="btn btn-primary !border-0 !rounded-lg"
                loading={savingId === entryToFinish?.id}
            />
        </>
    )
}
